import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import React from "react";
import { ShopRequest } from "../../../types/shop";
import { DataTable } from "../../../components/common/DataTable/DataTable";
import { getShopPreferenceColor, getShopPreferenceLabel, getShopRequestStatusColor, getShopRequestStatusLabel } from "../../../utils/statusColors";

interface ShopRequestsListProps {
  requests: ShopRequest[];
  isLoading: boolean;
  title: string;
  onApprove?: (request: ShopRequest) => void;
  onReject?: (request: ShopRequest) => void;
  onShowHistory?: (request: ShopRequest) => void;
}

const statusLabel: Record<number, string> = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
};

const preferenceLabel: Record<number, string> = {
  0: "Trusted",
  1: "DNU",
};

const requesterCell = (name?: string | null, email?: string | null) => {
  if (!name && !email) return "—";
  if (name && email) return `${name} (${email})`;
  return name ?? email ?? "—";
};

const tenantCell = (tenantName?: string | null, tenantId?: number | null) => {
  if (tenantName) return tenantName;
  if (tenantId) return `Tenant #${tenantId}`;
  return "—";
};

const ShopRequestsList: React.FC<ShopRequestsListProps> = ({
  requests,
  isLoading,
  title,
  onApprove,
  onReject,
  onShowHistory,
}) => {
  const columns = [
    {
      field: "requestedAt",
      headerName: "Requested At",
      flex: 0.8,
      renderCell: (value: string) =>
        new Date(value).toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      field: "shopName",
      headerName: "Shop",
      flex: 1.1,
      renderCell: (_: any, row: ShopRequest) =>
        row.shopName
          ? `${row.shopName} (#${row.shopId})`
          : `Shop #${row.shopId}`,
    },
    {
      field: "targetPreference",
      headerName: "Target",
      flex: 0.6,
      renderCell: (value: number) => (
        <span
          style={{
            color: getShopPreferenceColor(value),
            fontWeight: "bold",
          }}
        >
          {getShopPreferenceLabel(value)}
        </span>
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1.2,
      renderCell: (value: string | null | undefined) => value || "—",
    },
    {
      field: "requestedByFullName",
      headerName: "Requested By",
      flex: 1,
      renderCell: (_: any, row: ShopRequest) =>
        requesterCell(row.requestedByFullName, row.requestedByEmail),
    },
    {
      field: "requestedByTenantName",
      headerName: "Tenant",
      flex: 0.8,
      renderCell: (_: any, row: ShopRequest) =>
        tenantCell(row.requestedByTenantName, row.requestedByTenantId ?? null),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      renderCell: (value: number) => (
        <span
          style={{
            color: getShopRequestStatusColor(value),
            fontWeight: "bold",
          }}
        >
          {getShopRequestStatusLabel(value)}
        </span>
      ),
    },
  ];

  const actions =
    onApprove && onReject
      ? [
          {
            icon: <CheckIcon fontSize="small" />,
            label: "Approve",
            onClick: onApprove,
            color: "success" as const,
          },
          {
            icon: <CloseIcon fontSize="small" />,
            label: "Reject",
            onClick: onReject,
            color: "error" as const,
          },
          ...(onShowHistory
            ? [
                {
                  icon: <HistoryIcon fontSize="small" />,
                  label: "History",
                  onClick: onShowHistory,
                  color: "info" as const,
                },
              ]
            : []),
        ]
      : [];

  return (
    <DataTable<ShopRequest>
      items={requests}
      columns={columns}
      actions={actions}
      isLoading={isLoading}
      title={title}
    />
  );
};

export default ShopRequestsList;
